import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { ClientOriginView } from "../components/modal/clientOriginModel";

/** One interaction (booking/lead/cancellation) in a client's history. */
export interface ClientInteraction {
  id: number;
  date: string;
  time?: string | null;
  /** Реальное нестандартное время (Б3): показывать его, а не слот. */
  custom_time?: string | null;
  /** 'booking' | 'lead' — бейджи Игра/Бронь/Лид/Отмена (Б2). */
  type?: string | null;
  /** 30.09: 'team' — игра в ЧУЖОЙ команде (номер клиента в вейвере чужой брони). */
  via?: "team" | null;
  status: string;
  expected_revenue?: number | null;
  players?: number | null;
  prepaid?: number | null;
  prepaid_amount?: number | null;
  source?: string | null;
  booking_comment?: string | null;
  tea?: number | null;
  mc?: number | null;
  short_name?: string | null;
  report_total?: number | null;
  report_cash?: number | null;
  report_comment?: string | null;
  report_activity?: string | null;
  quest_price?: number | null;
  tea_price?: number | null;
  mc_price?: number | null;
  operator_id?: string | null;
  operator_name?: string | null;
}

/** Запрошенная бронь в ответе by-booking (20.08): шапка «Эта бронь» в карточке. */
export interface CurrentBookingLite {
  id: number;
  date: string;
  time?: string | null;
  custom_time?: string | null;
  players?: number | null;
  prepaid_amount?: number | null;
  source?: string | null;
  comment?: string | null;
  client_name?: string | null;
  short_name?: string | null;
  /** 28.09: кто и когда писал/правил коммент ДК этой брони */
  comment_log?: { at: string; by: string; from: string; to: string }[];
}

/** Досье (20.08, Саша): связанные с клиентом Telegram-люди — канал, метки, Unifans. */
export interface DossierMate {
  /** null — карточка вне смены: контакты вычищены (stripContacts, 13.10). */
  telegram_id: number | null;
  is_owner?: number | null;
  name?: string | null;
  username?: string | null;
  verified?: number | null;
  linked?: number | null;
  linked_via?: string | null;
  games_with: number;
  games_total: number;
  uc_earned: number;
  in_channel?: number | null;
  joined_channel: number;
  tag_minor?: number | null; tag_adult?: number | null; tag_competitor?: number | null;
  excluded?: number | null; sabotage?: number | null; staff_mark?: number | null;
  blocked_at?: string | null;
}

/** Досье: игроки из отчётов операторов (имя/др/теги/капитан). */
export interface DossierPlayer {
  name: string;
  birth_date?: string | null;
  captain?: number | null;
  tags?: string | null;
  times: number;
  last_date?: string | null;
}

export interface ClientProfile {
  /** null — клиент этой брони в базе не найден (ответ несёт только current). */
  id: string | number | null;
  name?: string | null;
  phone?: string | null;
  telegram?: string | null;
  note?: string | null;
  total_visits?: number | null;
  total_spent?: number | null;
  games_played?: number | null;
  /** Ожидаемая выручка несыгранных активных броней — НЕ «потратил» (Б2). */
  pending_amount?: number | null;
  bookings_count?: number | null;
  leads_count?: number | null;
  cancellations_count?: number | null;
  /** 30.09: сколько игр в чужих командах нашлось по вейверам (всего) и 20 свежих из них. */
  team_games_count?: number | null;
  team_history?: ClientInteraction[];
  /** 27.10: всего записей ленты (своих + в чужих командах) — для «История N»; сама лента урезана. */
  history_total?: number | null;
  favorite_quest?: string | null;
  last_visit?: string | null;
  history: ClientInteraction[];
  /** Только в by-booking: данные запрошенной брони. */
  current?: CurrentBookingLite | null;
  team?: DossierMate[];
  /** Телеграмы из самих броней клиента (25.08) — не Юнифанс-связки. */
  booking_telegrams?: string[];
  roster?: DossierPlayer[];
  /** 27.10 (Квестгид): «С нами с» и «Бронирует за» — по тем же броням и телефонам, что история; null — данных нет. */
  origin?: ClientOriginView | null;
}

/** Fetch a single client's full profile + interaction history (GET /api/clients/:id). */
export function useClientProfile(id: string | number | null) {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id == null) { setProfile(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    api<ClientProfile>(`/api/clients/${id}`)
      .then((r) => { if (alive) { setProfile({ ...r, history: r.history ?? [] }); setLoading(false); } })
      .catch((e: unknown) => { if (alive) { setError(e instanceof Error ? e.message : "Не удалось загрузить профиль"); setLoading(false); } });
    return () => { alive = false; };
  }, [id]);

  return { profile, loading, error };
}

/**
 * Профиль клиента по id брони (GET /api/clients/by-booking/:bookingId).
 * Для кнопки «Инфо» в модалке редактирования — у брони на фронте нет client_id,
 * резолвим на сервере. profile === null, если у брони нет привязанного клиента.
 */
/** Профиль по номеру (15.08): клиент из clients ЛИБО собранный по броням этого
 *  номера (GET /api/clients/by-phone без probe); null — по номеру ничего нет. */
export function useClientProfileByPhone(phone: string | null) {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!phone) { setProfile(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    api<ClientProfile | null>(`/api/clients/by-phone?phone=${encodeURIComponent(phone)}`)
      .then((r) => { if (alive) { setProfile(r ? { ...r, history: r.history ?? [] } : null); setLoading(false); } })
      .catch((e: unknown) => { if (alive) { setError(e instanceof Error ? e.message : "Не удалось загрузить профиль"); setLoading(false); } });
    return () => { alive = false; };
  }, [phone]);
  return { profile, loading, error };
}

export function useClientProfileByBooking(bookingId: number | null) {
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (bookingId == null) { setProfile(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    api<ClientProfile | null>(`/api/clients/by-booking/${bookingId}`)
      .then((r) => { if (alive) { setProfile(r ? { ...r, history: r.history ?? [] } : null); setLoading(false); } })
      .catch((e: unknown) => { if (alive) { setError(e instanceof Error ? e.message : "Не удалось загрузить профиль"); setLoading(false); } });
    return () => { alive = false; };
  }, [bookingId]);

  return { profile, loading, error };
}
